import React, { useState, useEffect } from 'react';
import { Form, Icon, Input, Button, message, Checkbox, Alert } from 'antd';
import { useAuth } from '../commons/contexts/auth-context';
import GroupSelectorModal from '../components/GroupSelectorModal';
import TwoFactorAuthModal from 'components/TwoFactorAuthModal';
import logo from '../assets/images/wh-logo-withstripes.png';
import {
  storeCookie,
  removeRememberComp,
  removeRememberUser,
  checkForCookie,
  getCookieValues,
} from '../commons/utils/auth-utils';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import client from '../commons/utils/api-client';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { clearPerformanceCookies } from 'commons/utils/commonUtils';

function Login(props) {
  const {
    login,
    groups,
    inactiveLoggedMsg,
    showInactivityMsg,
    forceLogout,
  } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [defaultCheck, setDefaultCheck] = useState(checkForCookie());
  const [customizerOptions, setCustomizerOptions] = useState({});
  const [autoplaySpeed, setAutoplaySpeed] = useState(5000);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [userUid, setUserUid] = useState('');
  const [loginValues, setLoginValues] = useState({});
  const { form } = props;

  const options = [
    { label: 'Remember company', value: 'company' },
    { label: 'Remember username', value: 'username' },
  ];

  const textSize = {
    small: '18px',
    medium: '22px',
    large: '30px',
  };

  const textStyles = type => {
    let options = customizerOptions.login_banner_text_styles || [];
    let bold = options.includes('bold');
    let italics = options.includes('italic');
    let underline = options.includes('underline');

    switch (type) {
      case 'fontStyle':
        return italics ? 'italic' : undefined;
      case 'textDecoration':
        return underline ? 'underline' : undefined;
      case 'fontWeight':
        return bold ? 'bold' : undefined;
    }
  };

  const bannerOptions = {
    fontSize: textSize[customizerOptions.login_banner_text_size] || '22px',
    color: customizerOptions.login_banner_text_color || '#fff',
    backgroundColor: customizerOptions.login_banner_bkgrnd_color || '#ed3835',
    fontStyle: textStyles('fontStyle'),
    textDecoration: textStyles('textDecoration'),
    fontWeight: textStyles('fontWeight'),
  };

  const settings = {
    dots: false,
    infinite: true,
    arrows: false,
    fade: false,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: autoplaySpeed,
    pauseOnHover: true,
  };

  const customOptions = React.useCallback(async () => {
    try {
      const res = await client('/loginCustomizerOptions', {
        method: 'POST',
        body: {
          options: [
            'login_show_banner',
            'login_lock_title',
            'login_static_title',
            'login_banner_text',
            'login_slider',
            'login_slide_speed',
            'login_dynamic_titles',
            'login_banner_link',
            'login_banner_link_url',
            'login_banner_bkgrnd_color',
            'login_banner_text_color',
            'login_banner_text_styles',
            'login_banner_text_size',
            'login_slider_links',
          ],
        },
      });

      let obj = {};
      res.data.forEach(opt => {
        if (
          opt.option_name === 'login_slider' ||
          opt.option_name === 'login_banner_text_styles'
        ) {
          obj[opt.option_name] = JSON.parse(opt.option_value);
        } else if (opt.option_name === 'login_slide_speed') {
          const speed = parseInt(opt.option_value) * 1000;
          obj[opt.option_name] = speed;
        } else {
          obj[opt.option_name] = opt.option_value;
        }
      });

      setCustomizerOptions(obj);
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    customOptions();
  }, [customOptions]);

  // once custom values are fetched from db, modifies the autoplaySpeed in settings
  useEffect(() => {
    if (customizerOptions.hasOwnProperty('login_slide_speed')) {
      setAutoplaySpeed(customizerOptions.login_slide_speed);
    }
  }, [customizerOptions]);

  useEffect(() => {
    let vals = getCookieValues();

    vals.forEach(element => {
      if (element.key === 'company') {
        form.setFieldsValue({
          company_url: element.value,
        });
      }
      if (element.key === 'username') {
        form.setFieldsValue({
          username: element.value,
        });
      }
    });
  }, []);

  const handleSubmit = e => {
    e.preventDefault();
    clearPerformanceCookies();
    sessionStorage.removeItem('company-logo-img');

    form.validateFields(async (err, values) => {
      if (!err) {
        try {
          let userLoginInfo = await client('userLoginInfo', {
            method: 'POST',
            body: {
              username: values.username,
              company_url: values.company_url,
            },
          }).then(async res => {
            setUserUid(res.user_uid);
            setLoginValues(values);
            if (res.twoFactorEnabled === true) {
              await client('send2faEmail', {
                method: 'POST',
                body: {
                  user_uid: res.user_uid,
                },
              })
                .then(async res => {
                  message.success(res.message);
                  setAuthModalVisible(true);
                })
                .catch(e => {
                  message.error(e.message);
                });
            } else {
              setSubmitting(true);

              await login(values);

              message.success('Login Success');
              rememberMe();

              setSubmitting(false);
            }
          });
        } catch (e) {
          setSubmitting(false);

          message.error(`Login failed: ${e.message}`);
        }
      }
    });
  };

  function cancelAuthModal() {
    setLoginValues({});
    setUserUid('');
    setAuthModalVisible(false);
  }

  function onChange(val) {
    setDefaultCheck(val);
  }

  function rememberMe() {
    if (defaultCheck.length > 0) {
      if (defaultCheck.find(el => el === 'company')) {
        if (
          form.getFieldValue('company_url') !== undefined &&
          form.getFieldValue('company_url') !== ''
        ) {
          storeCookie('comp', form.getFieldValue('company_url'));
        }
      } else {
        removeRememberComp();
      }
      if (defaultCheck.find(el => el === 'username')) {
        if (
          form.getFieldValue('username') !== undefined &&
          form.getFieldValue('username') !== ''
        ) {
          storeCookie('uname', form.getFieldValue('username'));
        }
      } else {
        removeRememberUser();
      }
    } else {
      removeRememberComp();
      removeRememberUser();
    }
  }

  async function cancelLogin() {
    forceLogout();
  }

  return (
    <div id="login-page">
      {customizerOptions.hasOwnProperty('login_show_banner') &&
      customizerOptions.login_show_banner === '1' ? (
        <div>
          {customizerOptions.hasOwnProperty('login_banner_link') &&
          customizerOptions.hasOwnProperty('login_banner_link_url') &&
          customizerOptions.login_banner_link === '1' ? (
            <a href={customizerOptions.login_banner_link_url} target="_blank">
              <div
                className="absolute w-100 login-banner"
                style={bannerOptions}
              >
                <h2>{customizerOptions.login_banner_text}</h2>
              </div>
            </a>
          ) : (
            <div className="absolute w-100 login-banner" style={bannerOptions}>
              <h2>{customizerOptions.login_banner_text}</h2>
            </div>
          )}
        </div>
      ) : null}
      <div id="login-columns-row" className="flex items-center">
        {groups.availables.length > 0 && !groups.current ? (
          <GroupSelectorModal cancelLogin={cancelLogin} />
        ) : null}

        <div id="login-slider">
          {customizerOptions.hasOwnProperty('login_show_banner') &&
          customizerOptions.login_show_banner === '1' ? (
            <div className="banner-buffer"></div>
          ) : null}
          {customizerOptions.hasOwnProperty('login_lock_title') &&
          customizerOptions.login_lock_title === '1' ? (
            <h2 className="login-slider-title">
              {customizerOptions.login_static_title}
            </h2>
          ) : null}
          <Slider {...settings}>
            {customizerOptions.hasOwnProperty('login_slider') &&
              customizerOptions.login_slider.map((image, i) => {
                return (
                  <div key={i}>
                    {customizerOptions.hasOwnProperty('login_dynamic_titles') &&
                    customizerOptions.login_dynamic_titles === '1' ? (
                      <h2 className="dynamic-title">{image.slide_title}</h2>
                    ) : null}
                    {customizerOptions.hasOwnProperty('login_slider_links') &&
                    customizerOptions.login_slider_links === '1' &&
                    image.link_url ? (
                      <a href={image.link_url} target="_blank">
                        <img alt={`slider-${i}`} src={image.url} />
                      </a>
                    ) : (
                      <img alt={`slider-${i}`} src={image.url} />
                    )}
                  </div>
                );
              })}
          </Slider>
        </div>
        <div id="login-form-container">
          <Form onSubmit={handleSubmit} className="login-form">
            <img
              src={logo}
              loading="lazy"
              alt="workhero-logo"
              className="logo-login"
            />
            <Form.Item>
              {form.getFieldDecorator('company_url', {
                rules: [
                  {
                    required: true,
                    message: "Please input your company's URL",
                  },
                ],
              })(
                <Input
                  prefix={
                    <Icon type="global" style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  placeholder="Company URL"
                />
              )}
            </Form.Item>

            <Form.Item>
              {form.getFieldDecorator('username', {
                rules: [
                  { required: true, message: 'Please input your username' },
                ],
              })(
                <Input
                  prefix={
                    <Icon type="user" style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  placeholder="Username"
                />
              )}
            </Form.Item>

            <Form.Item>
              {form.getFieldDecorator('password', {
                rules: [
                  { required: true, message: 'Please input your Password' },
                ],
              })(
                <Input.Password
                  prefix={
                    <Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  placeholder="Password"
                />
              )}
            </Form.Item>
            <Form.Item className="mb-1">
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="login-form-button"
              >
                Log in
              </Button>
            </Form.Item>
            <Link to="/forgot-password" id="forgotPass">
              Forgot Password?
            </Link>
          </Form>
          <div className="mx-auto">
            <Checkbox.Group
              onChange={val => onChange(val)}
              options={options}
              defaultValue={defaultCheck}
            />
            <p style={{ marginTop: '10px' }}>
              Learn more at{' '}
              <a
                href="https://www.getrufus.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                getrufus.com
              </a>
            </p>
            <div className="alt-center">
              <a href="https://www.getrufus.com/wearables">Solutions </a>
              <a href="https://www.getrufus.com/contact">Contact</a>
            </div>
            {inactiveLoggedMsg && (
              <Alert
                message="You were logged out due to inactivity.  If you'd like to change your settings for this, please log back in and click on My Profile and Inactivity Auto-Logout for Web."
                type="info"
                showIcon
                className="my-2 mx-2"
                closable
                onClose={() => showInactivityMsg(false)}
              />
            )}
          </div>
        </div>
      </div>
      <TwoFactorAuthModal
        visible={authModalVisible}
        onCancel={cancelAuthModal}
        user_uid={userUid}
        authCallback={() => login(loginValues)}
      />
    </div>
  );
}

export default Form.create({ name: 'normal_login' })(Login);
